import type { DataLock, OperationJob, OperationState, SyncFailure } from './revenueApi';

/** Опрос состояния операции: часто, пока данные закрыты или идет работа, и редко в простое */
export const POLL_ACTIVE_MS = 2000;
export const POLL_IDLE_MS = 20000;

export function isJobRunning(job: OperationJob | null): boolean {
  return !!job && job.status === 'running';
}

/** Данные аптек закрыты (блокировка действует) или в этом процессе идет операция */
export function isBusy(state: OperationState | null): boolean {
  return !!state && (!!state.lock || isJobRunning(state.job));
}

/** Через сколько миллисекунд читать состояние снова */
export function pollDelay(state: OperationState | null): number {
  return isBusy(state) ? POLL_ACTIVE_MS : POLL_IDLE_MS;
}

/** Доля выполненного от 0 до 100; null, пока сервер еще не знает общего объема работы */
export function progressPercent(job: OperationJob): number | null {
  const { done, total } = job.progress;
  if (!total) return null;
  return Math.max(0, Math.min(100, Math.round((done / total) * 100)));
}

/** «120 из 509» */
export function progressText(job: OperationJob): string {
  const { done, total } = job.progress;
  return total ? `${done} из ${total}` : 'подготовка';
}

/** Сколько минут и секунд осталось до автоматического снятия блокировки */
export function lockRemainingText(lock: DataLock, now: number): string {
  const seconds = Math.max(0, Math.ceil((lock.until - now) / 1000));
  if (seconds === 0) return 'сейчас истечет';
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return minutes > 0 ? `${minutes} мин ${rest < 10 ? '0' : ''}${rest} с` : `${rest} с`;
}

function two(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

/** «09.10.2026 14:05» в часовом поясе браузера */
export function formatMoment(ms: number): string {
  const d = new Date(ms);
  return `${two(d.getDate())}.${two(d.getMonth() + 1)}.${d.getFullYear()} ${two(d.getHours())}:${two(d.getMinutes())}`;
}

export interface FinishedNote {
  text: string;
  severity: 'success' | 'warning';
}

/** Что сказать пользователю, когда операция завершилась */
export function finishedNote(job: OperationJob): FinishedNote {
  if (job.status !== 'done') {
    return { text: job.message || `Операция «${job.title}» завершена не полностью`, severity: 'warning' };
  }
  const label = job.label ? `«${job.label}»` : '';
  if (job.op === 'recalc') {
    const values = job.result && typeof job.result.values === 'number' ? job.result.values : 0;
    return {
      text: values > 0 ? `История пересчитана: изменено значений ${values}` : 'История пересчитана, значения не изменились',
      severity: 'success'
    };
  }
  if (job.op === 'add_column') return { text: `Колонка ${label} добавлена`.replace('  ', ' '), severity: 'success' };
  return { text: `Колонка ${label} есть во всех таблицах аптек`.replace('  ', ' '), severity: 'success' };
}

export interface FailureGroup {
  kind: string;
  label: string;
  hint: string;
  tables: string[];
}

const FAILURE_ORDER = ['busy', 'rebuild', 'error', 'aborted'];

const FAILURE_TEXT: Record<string, { label: string; hint: string }> = {
  busy: {
    label: 'Таблица занята другими запросами',
    hint: 'Колонка не добавилась, потому что с таблицей в этот момент работали. Повторите операцию'
  },
  rebuild: {
    label: 'Нужна перестройка таблицы',
    hint: 'В таблице исчерпан лимит изменений без перестройки. Обратитесь к администратору БД'
  },
  error: {
    label: 'Ошибка при добавлении',
    hint: 'Подробности записаны в журнале действий'
  },
  aborted: {
    label: 'Операция остановлена',
    hint: 'Блокировка была снята вручную, до этих таблиц дело не дошло. Повторите операцию'
  }
};

/** Таблицы, в которые колонка не добавилась, сгруппированные по причине (busy, rebuild, error, aborted) */
export function groupFailures(failures: SyncFailure[]): FailureGroup[] {
  const byKind: Record<string, string[]> = {};
  failures.forEach((item) => {
    const kind = FAILURE_TEXT[item.kind] ? item.kind : 'error';
    if (!byKind[kind]) byKind[kind] = [];
    byKind[kind].push(item.table);
  });
  return FAILURE_ORDER.filter((kind) => byKind[kind] && byKind[kind].length > 0).map((kind) => ({
    kind,
    label: FAILURE_TEXT[kind].label,
    hint: FAILURE_TEXT[kind].hint,
    tables: byKind[kind]
  }));
}

/** Сколько таблиц показывать в списке причин, остальные сворачиваются в «и еще N» */
export const SHOWN_TABLES = 12;

export function tablesPreview(tables: string[]): string {
  const shown = tables.slice(0, SHOWN_TABLES).join(', ');
  const rest = tables.length - SHOWN_TABLES;
  return rest > 0 ? `${shown} и еще ${rest}` : shown;
}

// ---------------------------------------------------------------------------
// Пересчет истории: стартовая дата
// ---------------------------------------------------------------------------

export type RecalcPreset = 'year' | 'quarter' | 'all' | 'custom';

export const RECALC_PRESETS: Array<{ value: RecalcPreset; label: string }> = [
  { value: 'year', label: 'С начала года' },
  { value: 'quarter', label: 'Последние 3 месяца' },
  { value: 'all', label: 'Вся история' },
  { value: 'custom', label: 'С выбранной даты' }
];

/** Самая ранняя допустимая дата: раньше сервер не принимает */
export const RECALC_MIN_DATE = '2000-01-01';

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`;
}

/** Дата начала пересчета (YYYY-MM-DD) для пресета; для своей даты возвращается введенное значение */
export function presetDate(preset: RecalcPreset, custom: string, today: Date): string {
  if (preset === 'year') return `${today.getFullYear()}-01-01`;
  if (preset === 'quarter') {
    const start = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
    return isoDate(start);
  }
  if (preset === 'all') return RECALC_MIN_DATE;
  return custom;
}

/** Ошибка в дате начала пересчета или пустая строка */
export function recalcDateError(value: string, today: Date): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return 'Укажите дату начала пересчета';
  if (value < RECALC_MIN_DATE) return 'Дата не раньше 01.01.2000';
  if (value > isoDate(today)) return 'Нельзя пересчитывать будущие даты';
  return '';
}

/** 2026-10-09 -> 09.10.2026 */
export function displayDate(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : value;
}

/** Склонение «1 день, 2 дня, 5 дней» */
export function plural(n: number, one: string, few: string, many: string): string {
  const mod100 = n % 100;
  const mod10 = n % 10;
  if (mod100 >= 11 && mod100 <= 14) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

/** Число с разделением тысяч пробелом и разумным количеством знаков после запятой */
export function formatNumber(value: number): string {
  if (!isFinite(value)) return '-';
  const rounded = Math.round(value * 100) / 100;
  const [int, frac] = String(Math.abs(rounded)).split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${rounded < 0 ? '-' : ''}${grouped}${frac ? `,${frac}` : ''}`;
}
